import axios, { AxiosRequestConfig, AxiosPromise } from 'axios';
import endpoints from 'modules/user/endpoints';
import { Cookies } from 'react-cookie';
import { showLogoutAlert } from 'utils/helpers';
import { store } from 'store';
import { setUserDataAction } from 'modules/user';

export enum MICROSERVICES {
  AUTH = 'auth',
  STRATEGIES = 'strategies',
  CUSTOMERS = 'customers',
  CONTRACTS = 'contracts',
  DISTRIBUTIONS = 'distributions',
  REMUNERATIONS = 'remunerations',
}

axios.interceptors.response.use(
  async (response) => {
    if (response.config.url === endpoints.refreshToken()) {
      const cookies = new Cookies();
      const token = cookies.get('jwt');

      await store.dispatch(setUserDataAction(token));
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (originalRequest.url === endpoints.refreshToken()) {
      showLogoutAlert();
      throw error;
    }

    if (
      error.response &&
      error.response.status === 401 &&
      !originalRequest._retry &&
      originalRequest.url !== endpoints.login()
    ) {
      originalRequest._retry = true;
      try {
        await post(MICROSERVICES.AUTH, {
          url: endpoints.refreshToken(),
        });
        return axios(originalRequest);
      } catch (e) {
        showLogoutAlert();
        throw error;
      }
    }
    throw error;
  },
);

const request = <T>(
  service: MICROSERVICES,
  config: AxiosRequestConfig,
): AxiosPromise<T> => {
  return axios.request<T>({
    baseURL: `${process.env.REACT_APP_API_BASEURL}/${service}`,
    withCredentials: true,
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    },
    ...config,
  });
};

const get = <T>(
  service: MICROSERVICES,
  config: AxiosRequestConfig,
): AxiosPromise<T> => request(service, { ...config, method: 'GET' });

const post = <T>(
  service: MICROSERVICES,
  config: AxiosRequestConfig,
): AxiosPromise<T> => request(service, { ...config, method: 'POST' });

const put = <T>(
  service: MICROSERVICES,
  config: AxiosRequestConfig,
): AxiosPromise<T> => request(service, { ...config, method: 'PUT' });

const patch = <T>(
  service: MICROSERVICES,
  config: AxiosRequestConfig,
): AxiosPromise<T> => request(service, { ...config, method: 'PATCH' });

const del = <T>(
  service: MICROSERVICES,
  config: AxiosRequestConfig,
): AxiosPromise<T> => request(service, { ...config, method: 'DELETE' });

export { get, post, put, patch, del };
